import { Suspense, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { setAppElement } from 'react-modal';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { isEmpty } from 'lodash';

import PrivateRoute from 'components/common/Auth/Private';
import AuthCallback from 'components/common/Auth/Callback';
import Preloader from 'lib/Preloader';
import AppPageWrapper from 'components/common/layout/AppPageWrapper';
import { Store } from 'components/common/types/Store.types';
import { GoogleMapsLoaderProvider } from 'components/common/GoogleMaps';

import Auth from 'modules/Auth';
import { PermissionsEnum } from 'consts/permissions';
import getRoutes, { publicRoutes } from 'routes';
import Local from 'modules/Local';
import { useResellerMediaOwners } from 'customHooks/useResellerMediaOwners';
import { useDsps } from 'customHooks/useDsps';
import { useHasFeatureAccess } from 'customHooks/useHasFeatureAccess';
import { checkFeaturePermission } from 'utils/checkFeaturePermission';
import { useCreativeNotifications } from 'customHooks/useCreativeNotifications';
import { useApp } from './useApp';
import { AppConfigLoader } from './AppConfigLoader';
import { AppAuthContext } from './AppAuthContext';
import { FeatureFlags } from '../types/Features.types';

setAppElement('#root');

const App: React.FC = () => {
  useDsps();
  useResellerMediaOwners();
  useCreativeNotifications();
  const { isAuthenticated, isAuthChecked } = useApp();

  const environmentId = Local.getEnvironmentId();
  const location = useLocation();
  const bannerType = useSelector((state: Store) => state.banner.type);
  const publisherConfiguration = useSelector((state: Store) => state.publisher.configuration);
  const publisherFeatures = useSelector((state: Store) => state.publisher.publisherFeatures);

  const arePermissionsEnabled = useHasFeatureAccess(FeatureFlags.PLANNERS_PERMISSIONS);

  const isProgrammaticPermission = checkFeaturePermission(
    arePermissionsEnabled,
    Auth.hasPermission(PermissionsEnum.PROGRAMMATIC_PERMISSION),
  );
  const isDirectSalesPermission = checkFeaturePermission(
    arePermissionsEnabled,
    Auth.hasPermission(PermissionsEnum.DIRECT_SALES_PERMISSION),
  );

  const isPublisherData = !isEmpty(publisherConfiguration) && !isEmpty(publisherFeatures);
  const isPublicCampaignPlanRoute = location.pathname.startsWith('/campaign-plan/');
  const isComponentReadyToRender = isPublisherData || location.pathname.includes('/landing');
  const routes = isComponentReadyToRender ? getRoutes(isProgrammaticPermission, isDirectSalesPermission) : [];

  const isRequiredMarketData = Boolean(isPublisherData && environmentId && isAuthenticated);
  const isAuthenticatedView = isAuthenticated === true || location.pathname.includes('/accessdenied');

  const appAuthContextValue = useMemo(() => ({ isAuthChecked }), [isAuthChecked]);

  return (
    <GoogleMapsLoaderProvider>
      <AppAuthContext.Provider value={appAuthContextValue}>
        <AppPageWrapper
          isRequiredMarketData={isRequiredMarketData}
          bannerType={bannerType}
          isAuthenticatedView={isAuthenticatedView}
          isAuthChecked={isAuthChecked}
          isPublicCampaignPlanRoute={isPublicCampaignPlanRoute}
        >
          <Suspense fallback={<Preloader />}>
            <AppConfigLoader
              environmentId={environmentId}
              isAuthenticated={isAuthenticated}
              isAuthChecked={isAuthChecked}
              isComponentReadyToRender={isComponentReadyToRender}
              suspendWhileLoading={!isPublicCampaignPlanRoute}
            >
              <Routes>
                <Route path="/" element={<Navigate to="/landing" />} />
                <Route path="/auth-callback" element={<AuthCallback />} />
                {publicRoutes.map((route) => (
                  <Route key={route.id} path={route.path} element={<route.main />} />
                ))}
                {routes.map((route) => (
                  <Route
                    key={route.id}
                    path={route.path}
                    element={
                      <PrivateRoute
                        component={route.main as React.ComponentType}
                        pageAccessPermission={route.pageAccessPermission}
                      />
                    }
                  />
                ))}
              </Routes>
            </AppConfigLoader>
          </Suspense>
        </AppPageWrapper>
      </AppAuthContext.Provider>
    </GoogleMapsLoaderProvider>
  );
};

export default App;
